import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Card,
  Collapse,
  Tooltip,
  useTheme,
  alpha,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Scale as ScaleIcon,
  Diamond as DiamondIcon,
  LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DataContext } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../../styles/ag-grid-premium-theme.css';
import OTPModal from '../../components/OTPModal';
import { OTP_CONFIG } from '../../config/otpConfig';
import { useReactDevTools } from '../../context/ReactDevTools';

const OTP_BYPASS_CONFIG = {
  enabled: true,
  fields: ['huid'],
  customCheck: (changedFields, changes, originalData, newData) => {
    return false;
  }
};

const InventoryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSecretMode } = useReactDevTools();
  const { stock = [], loading, refresh, updateStock, deleteStock, companyDetails, grossStock, grossStockTransactions } = useContext(DataContext);
  console.log(grossStockTransactions)
  // State for master data from company details
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get item shortcut
  const getItemShortcut = useCallback((itemName) => {
    if (!itemName) return '';
    const item = itemCategories.find(cat =>
      cat.name.toLowerCase() === itemName.toLowerCase()
    );
    return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  }, [itemCategories]);

  // Helper function to get metal shortcut
  const getMetalShortcut = useCallback((metalName) => {
    if (!metalName) return '';
    const metal = metalTypes.find(m =>
      m.name.toLowerCase() === metalName.toLowerCase()
    );
    return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  }, [metalTypes]);

  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [originalItemData, setOriginalItemData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingItemData, setPendingItemData] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  // Silver Transaction Modal State
  const [silverModalOpen, setSilverModalOpen] = useState(false);
  // const activeTab = 0;
  // const [stockAddData, setStockAddData] = useState([]);
  // const [stockSoldData, setStockSoldData] = useState([]);
  // const [silverTypeStock, setSilverTypeStock] = useState({});
  // const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [duplicateCheckOpen, setDuplicateCheckOpen] = useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [processingDuplicates, setProcessingDuplicates] = useState({});
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    metals: [],
    soldStatus: []
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [gridApis, setGridApis] = useState({});
  const debounceTimer = useRef(null);

  // Glassmorphism styles from theme
  const glassStyles = {
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)'
    },
    modal: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    },
    card: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.8)'
        : 'rgba(30, 41, 59, 0.7)',
      backdropFilter: 'blur(15px)',
      WebkitBackdropFilter: 'blur(15px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 1,
      boxShadow: theme.shadows[4],
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[5]
      }
    },
    paper: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(30, 41, 59, 0.4)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 2,
      boxShadow: theme.shadows[4]
    },
    button: {
      light: {
        contained: {
          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
            boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.5)'
          }
        }
      },
      dark: {
        contained: {
          background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
          boxShadow: '0 4px 14px 0 rgba(96, 165, 250, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            boxShadow: '0 6px 20px 0 rgba(96, 165, 250, 0.45)'
          }
        }
      }
    }
  };

  // // Sorting function for tables
  // const handleSort = (key) => {
  //   let direction = 'asc';
  //   if (sortConfig.key === key && sortConfig.direction === 'asc') {
  //     direction = 'desc';
  //   }
  //   setSortConfig({ key, direction });

  //   const sortData = (data) => {
  //     console.log(data)
  //     return [...data].sort((a, b) => {
  //       if (a[key] < b[key]) {
  //         return direction === 'asc' ? -1 : 1;
  //       }
  //       if (a[key] > b[key]) {
  //         return direction === 'asc' ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   };

  //   if (activeTab === 0) {
  //     setStockAddData(sortData(stockAddData));
  //   } else {
  //     setStockSoldData(sortData(stockSoldData));
  //   }
  // };

  const handlePrintLabel = useCallback((item) => {
    console.log('=== PRINT LABEL DEBUG ===');
    console.log('Item:', item);
    console.log('Item.qr:', item?.qr);
    console.log('Item.serialNo:', item?.serialNo);

    if (!item?.qr) {
      console.warn('No QR code data found for item');
      setSnackbar({
        open: true,
        message: 'QR code data not found for this item',
        severity: 'warning'
      });
      return;
    }
    const printWindow = window.open('', '_blank');

    // Format the data for the label
    const formatNumber = (num) => {
      if (!num || num === "") return "0";
      return parseFloat(num).toFixed(3);
    };

    // Get metal short code using imported helper
    const getMetalShort = (metal) => {
      return getMetalShortcut(metal);
    };

    // Get item type short code using imported helper
    const getItemType = (name) => {
      if (!name) return '';

      // First check if it's a Miscellaneous item
      if (name.toLowerCase() === "miscellaneous" && item.miscitemShortcut) {
        return item.miscitemShortcut;
      }

      return getItemShortcut(name);
    };

    function wastageToAlphanumeric(wastage) {
      if (!wastage || wastage === "0" || wastage === "0.0") return "";

      const num = parseFloat(wastage);
      if (isNaN(num)) return "";

      // Handle whole numbers
      if (Number.isInteger(num)) {
        if (num <= 26) {
          // A-Z for 1-26
          return String.fromCharCode(64 + num);
        } else {
          // For numbers > 26, use number as is
          return num.toString();
        }
      }

      // Handle decimal numbers
      const wholePart = Math.floor(num);
      const decimalPart = Math.round((num - wholePart) * 100); // Get two decimal digits

      if (wholePart === 0) {
        // Just decimal part (e.g., 0.9 -> "9")
        return decimalPart.toString();
      }

      if (wholePart <= 26) {
        const letter = String.fromCharCode(64 + wholePart);
        if (decimalPart > 0) {
          return letter + decimalPart.toString();
        }
        return letter;
      } else {
        // For whole part > 26, use number + decimal
        if (decimalPart > 0) {
          return wholePart.toString() + decimalPart.toString();
        }
        return wholePart.toString();
      }
    }

    // Get purity from melting percentage
    const getPurity = (melting) => {
      if (!melting) return '92';
      const purity = Math.round(melting / 100 * 22);
      return purity.toString();
    };

    // Prepare data
    const formattedData = {
      serialNo: item.serialNo || '0',
      name: item.name || '',
      weight: formatNumber(item.weight),
      grossWeight: formatNumber(item.grossWeight),
      lessWeight: isNaN(formatNumber(item.lessWeight)) ? '0.000' : formatNumber(item.lessWeight),
      metal: getMetalShort(item.metal),
      huid: item.huid || '',
      makingCharges: item.makingCharges || '',
      wastage: wastageToAlphanumeric(item.wastage) || '0',
      purity: getPurity(item.melting),
      melting: item.melting,
      itemType: getItemType(item.name),
      supplierShortcut: item?.supplierShortcut
    };

    // Get the QR code directly from item.qr
    const qrData = item.qr || '';

    // Escape any quotes in the QR data for JavaScript
    const escapedQrData = qrData.replace(/"/g, '\\"').replace(/'/g, "\\'");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Jewelry Label - ${item.serialNo}</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: 8.1cm 1.2cm; /* EXACT size of the label */
            margin: 0 !important; /* Force no margins */
            padding: 0 !important;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
            width: 8.1cm;
            height: 1.2cm;
            font-family: Arial, sans-serif;
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            position: absolute;
            top: 0;
            left: 0;
            overflow: hidden;
          }

          .label-container {
            width: 8.1cm;
            height: 1.2cm;
            display: flex;
            position: absolute;
            top: 0;
            left: 0;
          }

          /* Dummy blank space (2.7 cm wide) */
          .blank-space {
            width: 2.7cm;
            height: 1.2cm;
          }

          /* Actual label content (5.4 cm × 1.2 cm) */
          .label {
            width: 5.4cm;
            height: 1.2cm;
            font-family: Arial, sans-serif;
            font-size: 7px;
            display: flex;
            box-sizing: border-box;
            padding: 0;
            position: relative;
          }

          .left,
          .right {
            width: 2.7cm;
            height: 1.2cm;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            box-sizing: border-box;
            margin-top: 3px !important;
          }

          .left {
            padding-left: 3px;
            text-align: left;
          }

          .left-inner {
            display: flex;
            flex-direction: row;
            align-items: center;
            width: 100%;
            height: 100%;
          }

          .qr-side {
            flex: 0 0 35%;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .text-side {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            padding-left: 2px;
          }

          .qr {
            width: 1cm;
            height: 1cm;
            background: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #999;
            font-size: 5px;
            overflow: hidden;
          }

          #qr-canvas {
            width: 100% !important;
            height: 100% !important;
            display: block;
          }

          .qr-text {
            font-size: 5px;
            text-align: center;
            line-height: 1;
          }

          .shop-name {
            font-size: 9px;
            font-weight: 900;
            color: black;
            text-align: center;
            width: 100%;
            line-height: 1.1;
          }

          .making {
            text-align: center;
            width: 100%;
            font-weight: 700;
            font-size: 8px;
            margin-top: 1px;
          }

          .item-code {
            font-size: 8px;
            font-weight: 750;
          }

          .right {
            padding-left: 8px;
            padding-top: 2px;
          }

          .details {
            font-size: 8px;
            font-weight: 750;
          }

          .weight {
            font-size: 8px;
            font-weight: 750;
          }

          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 8.1cm;
              height: 1.2cm;
            }

            body * {
              visibility: visible !important;
            }

            .label-container {
              position: absolute;
              top: 0;
              left: 0;
            }

            .label {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
        <!-- Load QRious library -->
        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
      </head>
      <body>
        <div class="label-container">
          <!-- Dummy blank space (2.7 cm wide) -->
          <div class="blank-space"></div>

          <!-- Actual label content (5.4 cm × 1.2 cm) -->
          <div class="label">
            <!-- Left part -->
            <div class="left">
              <div class="left-inner">
                <!-- Left side: QR -->
                <div class="qr-side">
                  <div id="pl-qr" class="qr">
                    <div class="qr-text">Generating QR...</div>
                  </div>
                </div>
                <!-- Right side: Text -->
                <div class="text-side">
                  <div class="shop-name">Ankita Jewellers</div>
                  <div class="making">
                    Making: <span id="makingChargesDisp">${formattedData.makingCharges}</span>%
                  </div>
                </div>
              </div>
            </div>

            <!-- Right part -->
            <div class="right">
              <div class="details">
                <span id="itemType">${formattedData.itemType}</span> 
                <span id="itemSrNo">${formattedData.serialNo}</span>
                <span id="itemSupplierShortcut"> | ${formattedData.supplierShortcut}</span>
                <span id="itemMetal"> | ${formattedData.metal}</span>
                <span id="itemPurity"> | ${formattedData.melting}</span>
              </div>
              <div class="weight">
                <span id="grossWeightSpan">GW-<span id="itemGrossWeight">${formattedData.grossWeight}</span></span>
                <span id="lessWeightSpan">LW-<span id="itemLessWeight">${formattedData.lessWeight}</span></span>
              </div>
              <div class="weight">NW - <span id="itemWeight">${formattedData.weight}</span></div>
              <div class="item-code">
                <span id="itemHuid">${formattedData.huid}</span> 
                <span id="wastageAlpha">${formattedData.wastage}</span>
              </div>
            </div>
          </div>
        </div>

        <script>
          // QR code data
          const qrData = "${escapedQrData}";

          console.log('QR Data received:', qrData);

          if (!qrData || qrData.trim() === '') {
            console.error('No QR data provided');
            document.getElementById('pl-qr').innerHTML = '<div class="qr-text">NO QR DATA</div>';
            setTimeout(printDocument, 500);
          } else {
            function generateQRCode() {
              console.log('Generating QR code using QRious for:', qrData);

              const qrContainer = document.getElementById('pl-qr');

              try {
                qrContainer.innerHTML = '';

                const canvas = document.createElement('canvas');
                canvas.id = 'qr-canvas';
                qrContainer.appendChild(canvas);

                if (typeof QRious === 'undefined') {
                  throw new Error('QRious library not loaded');
                }

                const qr = new QRious({
                  element: canvas,
                  value: qrData,
                  size: 180,
                  level: 'H',
                  background: 'white',
                  foreground: 'black',
                  backgroundAlpha: 1,
                  foregroundAlpha: 1,
                  padding: 2
                });

                console.log('QR Code generated successfully');

                canvas.style.width = '100%';
                canvas.style.height = '100%';

                setTimeout(printDocument, 500);

              } catch (error) {
                console.error('QR generation failed:', error);
                qrContainer.innerHTML = '<div class="qr-text" style="font-size: 5px;">QR: ' + 
                                        qrData.substring(0, 8) + 
                                        (qrData.length > 8 ? '...' : '') + '</div>';
                setTimeout(printDocument, 500);
              }
            }

            function generateSimpleQRCode() {
              const qrContainer = document.getElementById('pl-qr');
              qrContainer.innerHTML = '';

              const canvas = document.createElement('canvas');
              canvas.width = 60;
              canvas.height = 60;
              canvas.style.width = '100%';
              canvas.style.height = '100%';

              const ctx = canvas.getContext('2d');

              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, 60, 60);

              ctx.fillStyle = '#000000';

              ctx.fillRect(4, 4, 10, 10);
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(6, 6, 6, 6);
              ctx.fillStyle = '#000000';

              ctx.fillRect(46, 4, 10, 10);
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(48, 6, 6, 6);
              ctx.fillStyle = '#000000';

              ctx.fillRect(4, 46, 10, 10);
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(6, 48, 6, 6);
              ctx.fillStyle = '#000000';

              ctx.fillStyle = '#000000';
              ctx.font = '6px Arial';
              ctx.textAlign = 'center';
              ctx.fillText('QR', 30, 26);
              ctx.fillText('Code', 30, 34);

              qrContainer.appendChild(canvas);
              console.log('Simple QR placeholder created');
            }

            function printDocument() {
              console.log('Printing document...');

              // Force print settings
              window.print();

              setTimeout(() => {
                window.close();
              }, 1000);
            }

            function init() {
              if (typeof QRious !== 'undefined') {
                console.log('QRious library is loaded');
                generateQRCode();
              } else {
                console.log('Waiting for QRious library to load...');
                let attempts = 0;
                const maxAttempts = 10;

                function checkLibrary() {
                  attempts++;
                  if (typeof QRious !== 'undefined') {
                    console.log('QRious loaded after', attempts, 'attempts');
                    generateQRCode();
                  } else if (attempts < maxAttempts) {
                    setTimeout(checkLibrary, 100);
                  } else {
                    console.error('QRious library failed to load');
                    generateSimpleQRCode();
                    setTimeout(printDocument, 500);
                  }
                }

                checkLibrary();
              }
            }

            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', init);
            } else {
              init();
            }
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    setSnackbar({
      open: true,
      message: `Label for ${item.serialNo || item.name} printed successfully`,
      severity: 'success'
    });
  }, [getItemShortcut, getMetalShortcut]);

  // AG Grid column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: 'SERIAL NO',
      field: 'serialNo',
      width: 140,
      sortable: true,
      sort: "asc",
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: '600',
        color: theme.palette.text.primary,
        fontSize: '14px'
      },
      comparator: (valueA, valueB) => {
        return (parseInt(valueA) || 0) - (parseInt(valueB) || 0);
      }
    },
    {
      headerName: 'NAME',
      field: 'name',
      width: 140,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: '600',
        color: theme.palette.text.primary,
        fontSize: '14px'
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        const valA = nodeA.data?.name || '';
        const valB = nodeB.data?.name || '';

        if (valA === 'Miscellaneous' && valB === 'Miscellaneous') {
          const miscNameA = nodeA.data?.miscitemName || '';
          const miscNameB = nodeB.data?.miscitemName || '';
          return miscNameA.localeCompare(miscNameB);
        }

        return valA.localeCompare(valB);
      },
      valueGetter: (params) => {
        const name = params.data?.name || '';
        const miscitemName = params.data?.miscitemName || '';
        const miscitemShortcut = params.data?.miscitemShortcut || params.data?.itemShortcut || '';

        if (name === 'Miscellaneous' && miscitemName) {
          if (miscitemShortcut) {
            return `Miscellaneous (${miscitemName} - ${miscitemShortcut})`;
          }
          return `Miscellaneous (${miscitemName})`;
        }

        return name;
      }
    },
    {
      headerName: 'GROSS WEIGHT',
      field: 'grossWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'LESS WEIGHT',
      field: 'lessWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "" || params.value === "NaN") return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'WEIGHT',
      field: 'weight',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        fontWeight: '700',
        color: theme.palette.primary.main,
        fontSize: '14px'
      },
      comparator: (valueA, valueB) => {
        return parseFloat(valueA || 0) - parseFloat(valueB || 0);
      }
    },
    {
      headerName: 'METAL',
      field: 'metal',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        const metal = metalTypes.find(m => m.name === params.value);
        const shortcut = metal ? metal.shortcut : '';
        const isGold = params.value === 'Gold';
        return (
          <Chip
            label={shortcut ? `${params.value} (${shortcut})` : params.value}
            size="small"
            sx={{
              backgroundColor: isGold
                ? alpha('#f59e0b', 0.15)
                : alpha(theme.palette.secondary.main, 0.15),
              color: isGold ? '#92400e' : theme.palette.secondary.main,
              fontWeight: 600,
              border: `1px solid ${isGold ? '#fbbf24' : theme.palette.secondary.main}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'HUID',
      field: 'huid',
      width: 160,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        return (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              color: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderColor: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'STATUS',
      field: 'sold',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const isSold = params.value === "Yes";
        return (
          <Chip
            label={isSold ? "Sold" : "Available"}
            size="small"
            sx={{
              backgroundColor: isSold
                ? alpha('#10b981', 0.15)
                : alpha('#f59e0b', 0.15),
              color: isSold ? '#065f46' : '#92400e',
              fontWeight: 600,
              border: `1px solid ${isSold ? '#10b981' : '#f59e0b'}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'SUPPLIER',
      field: 'supplierName',
      width: 200,
      sortable: true,
      filter: true,
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'MELTING %',
      field: 'melting',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'SILVER RATE',
      field: 'silverRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'LABOUR',
      field: 'labour',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'GOLD RATE',
      field: 'goldCuttingRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'WASTAGE %',
      field: 'wastage',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'MAKING CHARGE',
      field: 'makingCharges',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    // NEW COLUMNS: Created At and Updated At
    {
      headerName: 'CREATED AT',
      field: 'created_at',
      width: 180,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        if (!params.value) {
          params.value = params.data?.createdAt;
        }
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } catch {
          return '-';
        }
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '13px',
        fontFamily: 'monospace'
      }
    },
    {
      headerName: 'UPDATED AT',
      field: 'updated_at',
      width: 180,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        if (!params.value) {
          params.value = params.data?.updatedAt;
        };
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } catch {
          return '-';
        }
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '13px',
        fontFamily: 'monospace'
      }
    },
    {
      headerName: 'ACTIONS',
      field: 'actions',
      width: 200,
      pinned: 'right',
      suppressMovable: true,
      cellRenderer: (params) => (
        <Box className="flex space-x-2">
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleEdit(params.data)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#ef4444', 0.1),
                color: '#ef4444',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha('#ef4444', 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleDelete(params.data)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Label" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#f59e0b', 0.1),
                color: '#f59e0b',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha('#f59e0b', 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handlePrintLabel(params.data)}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [theme, metalTypes, handlePrintLabel]);

  // AG Grid default column properties
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 120,
    suppressMenu: false,
    floatingFilter: false,
    wrapText: true,
    autoHeight: false,
    cellStyle: {
      color: theme.palette.text.primary,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell-premium'
  }), [theme.palette.text.primary]);

  // Handle search with debounce
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Search logic is handled in filteredData memo
    }, 300);
  }, []);

  const filteredData = useMemo(() => {
    if (!stock.length) return [];

    let result = stock;
    const searchLower = searchTerm.toLowerCase();

    // Apply search filter
    if (searchTerm) {
      result = result.filter(item => {
        if (item.serialNo && item.serialNo.toString().toLowerCase().includes(searchLower)) return true;
        if (item.name && item.name.toString().toLowerCase().includes(searchLower)) return true;
        if (item.huid && item.huid.toString().toLowerCase().includes(searchLower)) return true;
        if (item.description && item.description.toLowerCase().includes(searchLower)) return true;
        if (item.supplierName && item.supplierName.toLowerCase().includes(searchLower)) return true;
        if (item.weight && item.weight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.lessWeight && item.lessWeight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.grossWeight && item.grossWeight.toString().toLowerCase().includes(searchLower)) return true;
        return false;
      });
    }

    // Apply category filters
    if (filters.categories.length > 0) {
      result = result.filter(item => filters.categories.includes(item.name));
    }

    // Apply metal filters
    if (filters.metals.length > 0) {
      result = result.filter(item => filters.metals.includes(item.metal));
    }

    // Apply sold status filters
    if (filters.soldStatus.length > 0) {
      result = result.filter(item => {
        const isSold = item.sold === "Yes";
        return (filters.soldStatus.includes('sold') && isSold) ||
          (filters.soldStatus.includes('unsold') && !isSold);
      });
    }

    return result;
  }, [stock, searchTerm, filters]);

  // Calculate statistics - optimized
  const statistics = useMemo(() => {
    if (!stock.length) {
      return {
        totalItems: 0,
        soldItems: 0,
        unsoldItems: 0,
        totalWeight: '0.000',
        totalFine: '0.000'
      };
    }

    const totalItems = filteredData.length;
    const soldItems = filteredData.filter(item => item.sold === "Yes").length;
    const unsoldItems = totalItems - soldItems;

    let totalWeight = 0;
    let totalFine = 0;

    for (let i = 0; i < filteredData.length; i++) {
      const item = filteredData[i];
      const weight = parseFloat(item.weight) || 0;
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;

      totalWeight += weight;
      totalFine += weight * ((melting + wastage) / 100);
    }

    return {
      totalItems,
      soldItems,
      unsoldItems,
      totalWeight: totalWeight.toFixed(2),
      totalFine: totalFine.toFixed(2)
    };
  }, [filteredData, stock.length]);

  // Calculate category counts - optimized
  const categoryCounts = useMemo(() => {
    return stock.reduce((counts, item) => {
      const category = item.name || 'Uncategorized';
      counts[category] = (counts[category] || 0) + 1;
      return counts;
    }, {});
  }, [stock]);

  // Calculate metal counts - optimized
  const metalCounts = useMemo(() => {
    return stock.reduce((counts, item) => {
      const metal = item.metal || 'Unknown';
      counts[metal] = (counts[metal] || 0) + 1;
      return counts;
    }, {});
  }, [stock]);

  // Sold status counts
  const soldStatusCounts = useMemo(() => {
    return stock.reduce((acc, item) => {
      if (item.sold === "Yes") {
        acc.sold++;
      } else {
        acc.unsold++;
      }
      return acc;
    }, { sold: 0, unsold: 0 });
  }, [stock]);

  // Extract unique categories and metals - simplified
  const categories = useMemo(() => Object.keys(categoryCounts).sort(), [categoryCounts]);
  const metals = useMemo(() => Object.keys(metalCounts).sort(), [metalCounts]);

  // Group filtered data by category
  const filteredGroupedData = useMemo(() => {
    if (filteredData.length === 0) return {};

    return filteredData.reduce((groups, item) => {
      const category = item.name || 'Uncategorized';

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(item);
      return groups;
    }, {});
  }, [filteredData]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  // Handle category expansion
  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));

    setTimeout(() => {
      if (gridApis[category]) {
        gridApis[category].sizeColumnsToFit();
      }
    }, 100);
  };

  // useEffect(() => {
  //   console.log('Silver Stock:', silverStock);
  //   console.log('Silver Transactions:', silverTransactions);


  //   if (silverTransactions && silverTransactions.length > 0) {
  //     console.log('Processing silver transactions...', silverTransactions);
  //     // const addData = silverTransactions.filter(item => item?.silverTransaction?.[0]?.operation === "add");
  //     // const soldData = silverTransactions.filter(item => item?.silverTransaction?.[0]?.operation === "subtract");

  //     // const transformedAddData = addData.map(item => ({
  //     //   id: item.id || Math.random().toString(36).substr(2, 9),
  //     //   date: new Date(item.date || item.timestamp).toLocaleDateString('en-GB'),
  //     //   silverType: item?.silverType,
  //     //   netWeight: item?.netWeight,
  //     //   supplierName: item?.description || "Unknown Supplier"
  //     // }));

  //     // const transformedSoldData = soldData.map(item => ({
  //     //   id: item.id || Math.random().toString(36).substr(2, 9),
  //     //   date: new Date(item.date || item.timestamp).toLocaleDateString('en-GB'),
  //     //   silverType: item?.silverType,
  //     //   netWeight: item?.netWeight,
  //     //   billNo: item?.description || "N/A",
  //     //   customerName: item?.customerName || "Unknown Customer",
  //     //   itemName: item?.itemName || item?.description || "Unknown Item"
  //     // }));

  //     // setStockAddData(transformedAddData);
  //     // setStockSoldData(transformedSoldData);
  //   } else {
  //     // Fallback to sample data if no transactions
  //     // setStockAddData([]);
  //     // setStockSoldData([]);
  //   }
  // }, [silverTransactions, silverStock]);

  // Handle grid ready event
  const handleGridReady = (category, params) => {
    setGridApis(prev => ({
      ...prev,
      [category]: params.api
    }));
    params.api.sizeColumnsToFit();

    // Set initial row selection
    const selectedIds = new Set(selectedItems);
    params.api.forEachNode(node => {
      node.setSelected(selectedIds.has(node.data.id));
    });
  };

  // Handle grid selection change
  const handleSelectionChanged = (category, items) => {
    return (params) => {
      const selectedRows = params.api.getSelectedRows();
      const selectedIds = selectedRows.map(row => row.id);
      const allItemIds = items.map(item => item.id);

      setSelectedItems(prev => {
        const newSelected = new Set(prev);

        // Add newly selected items
        selectedIds.forEach(id => newSelected.add(id));

        // Remove items from this category that are no longer selected
        allItemIds.forEach(id => {
          if (!selectedIds.includes(id)) {
            newSelected.delete(id);
          }
        });

        return Array.from(newSelected);
      });
    };
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setOriginalItemData({ ...item });
    setEditFormData({ ...item });
    setEditDialogOpen(true);
  };

  const handleSave = (itemId) => {
    const changes = {};
    const changedFields = [];

    // Track all changes
    Object.keys(editFormData).forEach(key => {
      const originalValue = originalItemData[key] || '';
      const newValue = editFormData[key] || '';

      if (originalValue.toString() !== newValue.toString()) {
        changes[key] = {
          from: originalValue,
          to: newValue
        };
        changedFields.push(key);
      }
    });

    // Check if update should bypass OTP
    const shouldBypassOTP = checkOTPBypass(changedFields, changes, originalItemData, editFormData);

    if (shouldBypassOTP) {
      console.log('OTP bypass condition met, executing direct update');
      console.log('Changed fields:', changedFields);
      executeDirectUpdate(itemId, editFormData, changes);
    } else {
      // Require OTP for other updates
      const pendingData = {
        type: 'edit',
        itemId: itemId,
        original: originalItemData,
        updated: editFormData,
        changes: changes,
        changedFields: changedFields
      };

      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION, JSON.stringify({
        type: 'edit',
        itemId: itemId,
        originalData: originalItemData,
        updatedData: editFormData
      }));

      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM, JSON.stringify({
        original: originalItemData,
        updated: editFormData
      }));

      setPendingAction('edit');
      setPendingItemData(pendingData);
      setOtpModalOpen(true);
    }
  };

  // New function to check if OTP should be bypassed
  const checkOTPBypass = (changedFields, changes, originalData, newData) => {
    if (!OTP_BYPASS_CONFIG.enabled) return false;

    // Case 1: Exactly one field changed and it's in the bypass list
    if (changedFields.length === 1) {
      const changedField = changedFields[0];
      if (OTP_BYPASS_CONFIG.fields.includes(changedField)) {
        return true;
      }
    }

    // Case 2: Multiple fields changed but all are in bypass list
    // (e.g., updating both HUID and SupplierName at once)
    if (changedFields.length > 1) {
      const allFieldsBypassable = changedFields.every(field =>
        OTP_BYPASS_CONFIG.fields.includes(field)
      );
      if (allFieldsBypassable) {
        return true;
      }
    }

    // Case 3: Custom check for more complex conditions
    if (OTP_BYPASS_CONFIG.customCheck) {
      return OTP_BYPASS_CONFIG.customCheck(changedFields, changes, originalData, newData);
    }

    return false;
  };

  // Keep the executeDirectUpdate function as is
  const executeDirectUpdate = async (itemId, updatedData, changes) => {
    setIsProcessingAction(true);

    try {
      console.log('Executing direct update for item:', itemId);
      console.log('Changed fields:', Object.keys(changes));

      const response = await updateStock(itemId, updatedData);
      console.log('Update response:', response);

      if (!response?.success) {
        throw new Error('Failed to update item');
      }

      const changedFieldNames = Object.keys(changes).join(', ');

      setSnackbar({
        open: true,
        message: `${changedFieldNames} updated successfully`,
        severity: 'success'
      });

      // Close edit dialog
      setEditDialogOpen(false);
      setEditingItem(null);
      setEditFormData({});
      setOriginalItemData({});

      // Refresh data to show updated values
      await refresh();

    } catch (error) {
      console.error('Direct update failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to update: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Optimized duplicate check function
  const checkDuplicateSerialNumbers = useCallback(async () => {
    setIsCheckingDuplicates(true);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        console.time('Duplicate Check');

        const categorySerialMap = new Map();

        for (let i = 0; i < stock.length; i++) {
          const item = stock[i];

          if (item.sold === "Yes") continue;

          const serialNo = item.serialNo;
          if (!serialNo || serialNo === "" || serialNo === "0") continue;

          const category = item.name || 'Uncategorized';
          const key = `${category}|${serialNo}`;

          if (!categorySerialMap.has(key)) {
            categorySerialMap.set(key, []);
          }
          categorySerialMap.get(key).push(item);
        }

        const duplicates = [];
        for (const [key, items] of categorySerialMap.entries()) {
          if (items.length > 1) {
            const [category, serialNo] = key.split('|');
            duplicates.push({ category, serialNo, items });
          }
        }

        setDuplicateItems(duplicates);

        if (duplicates.length > 0) {
          setDuplicateCheckOpen(true);
        } else {
          setSnackbar({
            open: true,
            message: '✓ No duplicate serial numbers found for unsold items!',
            severity: 'success'
          });
        }

        console.timeEnd('Duplicate Check');
      } catch (error) {
        console.error('Error checking duplicates:', error);
        setSnackbar({
          open: true,
          message: 'Error checking duplicates. Please try again.',
          severity: 'error'
        });
      } finally {
        setIsCheckingDuplicates(false);
      }
    }, 50); // Small delay to prevent UI blocking
  }, [stock]);


  // Create a debounced version of the duplicate check
  const debouncedDuplicateCheck = useMemo(
    () => {
      let timeoutId;
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          checkDuplicateSerialNumbers();
        }, 100);
      };
    },
    [checkDuplicateSerialNumbers]
  );

  // Improved function to find next available serial number within a specific category (only for unsold items)
  // Create a memoized map of used serial numbers by category
  const usedSerialNumbersMap = useMemo(() => {
    const map = new Map();

    for (const item of stock) {
      if (item.sold !== "Yes" && item.serialNo) {
        const category = item.name || 'Uncategorized';
        if (!map.has(category)) {
          map.set(category, new Set());
        }
        map.get(category).add(parseInt(item.serialNo));
      }
    }

    return map;
  }, [stock]);

  // Simplified findNextAvailableSerialNumber
  const findNextAvailableSerialNumber = useCallback((category) => {
    const usedSerials = usedSerialNumbersMap.get(category) || new Set();
    let nextNumber = 1;

    while (usedSerials.has(nextNumber)) {
      nextNumber++;
    }

    return nextNumber.toString().padStart(3, '0');
  }, [usedSerialNumbersMap]);

  // Function to change serial number of a specific item
  const handleChangeSerialNumber = async (item, duplicateCategory, duplicateSerialNo) => {
    if (processingDuplicates[item.id]) return;

    setProcessingDuplicates(prev => ({ ...prev, [item.id]: true }));

    try {
      // Find next available serial number for this category (considering only unsold items)
      const nextSerial = await findNextAvailableSerialNumber(duplicateCategory);

      // Prepare updated data
      const updatedData = {
        ...item,
        serialNo: nextSerial
      };

      // Update the item
      const response = await updateStock(item.id, updatedData);

      if (response?.success) {
        setSnackbar({
          open: true,
          message: `Serial number changed from ${item.serialNo} to ${nextSerial} for ${item.name || item.serialNo}`,
          severity: 'success'
        });

        // Refresh data to reflect changes
        await refresh();

        // Update the duplicate list
        setDuplicateItems(prevDuplicates => {
          return prevDuplicates.map(dupGroup => {
            if (dupGroup.category === duplicateCategory && dupGroup.serialNo === duplicateSerialNo) {
              const updatedItems = dupGroup.items.filter(i => i.id !== item.id);
              if (updatedItems.length <= 1) {
                // This group is no longer a duplicate, filter it out
                return null;
              }
              return { ...dupGroup, items: updatedItems };
            }
            return dupGroup;
          }).filter(group => group !== null);
        });
      } else {
        throw new Error('Failed to update serial number');
      }
    } catch (error) {
      console.error('Error changing serial number:', error);
      setSnackbar({
        open: true,
        message: `Failed to change serial number: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingDuplicates(prev => ({ ...prev, [item.id]: false }));
    }
  };

  // Update the DuplicateCheckDialog component with better messaging
  const DuplicateCheckDialog = () => {
    return (
      <Dialog
        open={duplicateCheckOpen}
        onClose={() => setDuplicateCheckOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(148, 163, 184, 0.2)',
            borderRadius: '20px',
            boxShadow: theme.shadows[6]
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: '45px',
                height: '45px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`
              }}>
                <SearchIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Duplicate Serial Numbers Detected
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {duplicateItems.length} issue(s) found with unsold items
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setDuplicateCheckOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {duplicateItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)} 0%, ${alpha(theme.palette.success.light, 0.2)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <SearchIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>
                No Duplicates Found!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                All unsold items have unique serial numbers within their categories.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={() => setDuplicateCheckOpen(false)}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none'
                  }}
                >
                  Close
                </Button>
                {/* <Button
                  variant="contained"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleFixAllDuplicates}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                >
                  Fix All Duplicates
                </Button> */}
              </Box>

              <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: '10px' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  ⚠️ <strong>Note:</strong> Only <strong>UNSOLD</strong> items are checked for duplicates.
                  Sold items can have the same serial number for record keeping.
                  Different categories can have the same serial numbers.
                </Typography>
              </Box>

              {duplicateItems.map((duplicateGroup, index) => (
                <Paper
                  key={index}
                  sx={{
                    mb: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    borderRadius: '12px',
                    background: alpha(theme.palette.error.main, 0.05)
                  }}
                >
                  <Box sx={{
                    p: 2,
                    background: alpha(theme.palette.error.main, 0.1),
                    borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                      ⚠️ Category: {duplicateGroup.category} | Duplicate Serial: {duplicateGroup.serialNo}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {duplicateGroup.items.length} unsold items share this serial number in the same category
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1) }}>
                            <TableCell sx={{ fontWeight: 700 }}>Item ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Current Serial No</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>HUID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Metal</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {duplicateGroup.items.map((item, itemIndex) => (
                            <TableRow key={item.id} hover>
                              <TableCell>{item.id?.slice(-6) || item.serialNo}</TableCell>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.serialNo}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                                    color: theme.palette.error.main,
                                    fontWeight: 700,
                                    fontFamily: 'monospace'
                                  }}
                                />
                              </TableCell>
                              <TableCell>{item.huid || '-'}</TableCell>
                              <TableCell>{item.metal}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.sold === "Yes" ? "Sold" : "Unsold"}
                                  size="small"
                                  sx={{
                                    backgroundColor: item.sold === "Yes"
                                      ? alpha(theme.palette.info.main, 0.2)
                                      : alpha(theme.palette.warning.main, 0.2),
                                    color: item.sold === "Yes"
                                      ? theme.palette.info.main
                                      : theme.palette.warning.main
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {itemIndex === 0 ? (
                                  <Chip
                                    label="Keep Original"
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                                      color: theme.palette.success.main
                                    }}
                                  />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={processingDuplicates[item.id] ? <CircularProgress size={16} /> : <EditIcon />}
                                    onClick={() => handleChangeSerialNumber(item, duplicateGroup.category, duplicateGroup.serialNo)}
                                    disabled={processingDuplicates[item.id] || item.sold === "Yes"}
                                    sx={{
                                      borderColor: theme.palette.warning.main,
                                      color: theme.palette.warning.main,
                                      '&:hover': {
                                        borderColor: theme.palette.warning.dark,
                                        backgroundColor: alpha(theme.palette.warning.main, 0.1)
                                      }
                                    }}
                                  >
                                    {processingDuplicates[item.id] ? 'Changing...' : 'Change Serial No'}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              ))}

              {/* <Box sx={{
                p: 2,
                background: alpha(theme.palette.info.main, 0.1),
                borderRadius: '10px',
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  💡 <strong>How it works:</strong>
                  <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Only <strong>UNSOLD</strong> items within the <strong>SAME CATEGORY</strong> are checked for duplicates</li>
                    <li>Different categories can have the same serial numbers (e.g., Ring #001 and Necklace #001)</li>
                    <li>Sold items are ignored as they're historical records</li>
                    <li>The first unsold item keeps its serial number, duplicates get new numbers</li>
                    <li>New serial numbers are automatically assigned as the next available number in that category</li>
                  </ul>
                </Typography>
              </Box> */}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          <Button onClick={() => setDuplicateCheckOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };



  // Add this helper function
  const getBypassFieldsHelpText = (fieldName) => {
    const bypassFieldsMap = {
      huid: "HUID",
      // supplierName: "Supplier Name"
      // Add more mappings as you add fields
    };

    const fieldLabel = bypassFieldsMap[fieldName];
    if (!fieldLabel) return "";

    // Check if this is the only field being edited
    const fieldChanged = editFormData[fieldName] !== originalItemData[fieldName];
    const otherChanges = Object.keys(editFormData).some(key =>
      key !== fieldName && editFormData[key] !== originalItemData[key]
    );

    if (fieldChanged && !otherChanges) {
      return `${fieldLabel} updates don't require OTP verification`;
    }
    return "";
  };

  const handleOTPVerificationSuccess = async () => {
    setIsProcessingAction(true);

    try {
      if (pendingAction === 'edit') {
        await executeEditAction();
      } else if (pendingAction === 'delete') {
        await executeDeleteAction();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to ${pendingAction} item: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsProcessingAction(false);

      if (pendingAction === 'edit') {
        setEditDialogOpen(false);
        setEditingItem(null);
        setEditFormData({});
        setOriginalItemData({});
      }

      // Reset OTP state
      setPendingAction(null);
      setPendingItemData(null);
    }
  };

  const executeEditAction = async () => {
    if (!pendingItemData) return;

    try {
      let response = await updateStock(pendingItemData.itemId, pendingItemData.updated);
      console.log('Update response:', response);
      if (!response?.success) {
        throw new Error('Failed to update item');
      }
      console.log('=== Item Edit Changes ===');
      console.log('Item ID:', pendingItemData.itemId);
      console.log('Original Data:', pendingItemData.original);
      console.log('Modified Data:', pendingItemData.updated);
      console.log('Changes:', pendingItemData.changes);
      console.log('=========================');

      setSnackbar({
        open: true,
        message: `Item ${pendingItemData.updated.serialNo || pendingItemData.updated.name} updated successfully`,
        severity: 'success'
      });

    } catch (error) {
      throw error;
    }
  };

  const executeDeleteAction = async () => {
    console.log('Deleting item ID:', pendingItemData);
    if (!pendingItemData) return;

    try {
      const response = await deleteStock(pendingItemData.id);
      console.log('Delete response:', response);

      if (!response?.success) {
        throw new Error('Failed to delete item');
      }

      setSnackbar({
        open: true,
        message: `Item ${pendingItemData.serialNo} deleted successfully`,
        severity: 'success'
      });

    } catch (error) {
      throw error;
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({});
    setOriginalItemData({});

    setSnackbar({
      open: true,
      message: 'Edit cancelled',
      severity: 'info'
    });
  };

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete item ${item.serialNo || item.name}?`)) {
      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION, JSON.stringify({
        type: 'delete',
        itemId: item.id,
        itemData: item
      }));

      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM, JSON.stringify(item));

      setPendingAction('delete');
      setPendingItemData(item);
      setOtpModalOpen(true);
    }
  };

  // Export function - matches HTML exactly
  const handleExport = () => {
    if (filteredData.length === 0) {
      setSnackbar({
        open: true,
        message: 'No data to export',
        severity: 'warning'
      });
      return;
    }

    try {
      // Sort data by category then serialNo (like HTML)
      const sortedData = [...filteredData].sort((a, b) => {
        if ((a.name || '').localeCompare(b.name || '') !== 0) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return (parseInt(a.serialNo) || 0) - (parseInt(b.serialNo) || 0);
      });

      // Create headers (same as HTML)
      const headers = [
        'Serial No.', 'Name', 'Weight', 'Gross Weight', 'Less Weight',
        'Buying Rate', 'Gold Cutting Rate', 'Wastage', 'Supplier Name',
        'Metal', 'Melting', 'HUID', 'Sold', 'Description', 'Item Detail'
      ];

      // Build rows with category headers (exactly like HTML)
      let csvRows = [];
      let currentCategory = '';

      sortedData.forEach(item => {
        if (item.name !== currentCategory) {
          // Add a category header row
          csvRows.push([`${item.name} (Category)`]);
          // Add column headers again under category
          csvRows.push(headers);
          currentCategory = item.name;
        }

        // Push actual item row (all fields like HTML)
        csvRows.push([
          item.serialNo || '',
          item.name || '',
          item.weight || '',
          item.grossWeight || '',
          item.lessWeight || '',
          item.buyingRate || '',
          item.goldCuttingRate || '',
          item.wastage || '',
          item.supplierName || '',
          item.metal || '',
          item.melting || '',
          item.huid || '',
          item.sold || '',
          item.description || '',
          item.itemDetail || ''
        ]);
      });

      // Convert to CSV
      const csvContent = csvRows
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `jewelry_inventory_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Data exported successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export data',
        severity: 'error'
      });
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refresh();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to refresh data',
        severity: 'error'
      });
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      categories: [],
      metals: [],
      soldStatus: []
    });
    setSelectedItems([]);

    // Clear all grid selections
    Object.values(gridApis).forEach(api => {
      if (api) {
        api.deselectAll();
      }
    });
  };

  // Select all items across all categories
  const handleSelectAll = () => {
    const allIds = filteredData.map(item => item.id);
    setSelectedItems(allIds);

    // Update grid selections
    Object.values(gridApis).forEach(api => {
      if (api) {
        api.selectAll();
      }
    });
  };

  // Clear all selections
  const handleClearSelection = () => {
    setSelectedItems([]);

    // Clear all grid selections
    Object.values(gridApis).forEach(api => {
      if (api) {
        api.deselectAll();
      }
    });
  };

  return (
    <Box className="min-h-screen">
      <Container maxWidth="xl" className="py-8">
        <Grid container spacing={3}>
          {/* Filters Sidebar */}
          <Grid item size={{ xs: 12, md: 3 }}>
            <Paper
              sx={{
                ...glassStyles.paper,
                padding: '24px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <Box className="flex items-center justify-between mb-4">
                <Typography
                  variant="h6"
                  className="font-bold flex items-center"
                  sx={{ color: 'text.primary' }}
                >
                  <FilterIcon className="mr-2" />
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    color: 'text.secondary',
                    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.secondary, 0.2)
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>

              {/* Selection Actions */}
              {selectedItems.length > 0 && (
                <Box
                  className="mb-4 p-3 rounded-lg"
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: '12px'
                  }}
                >
                  <Typography variant="subtitle2" className="font-semibold mb-2" sx={{ color: 'primary.main' }}>
                    {selectedItems.length} items selected
                  </Typography>
                  <Box className="flex space-x-2">
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        color: 'primary.main',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1)
                        }
                      }}
                      onClick={handleClearSelection}
                    >
                      Clear
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        borderRadius: '8px',
                        '&:hover': {
                          boxShadow: theme.shadows[4]
                        }
                      }}
                      onClick={handleSelectAll}
                    >
                      Select All ({filteredData.length})
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Search Bar */}
              <Box className="mb-6">
                <TextField
                  fullWidth
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: alpha(theme.palette.text.primary, 0.05),
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.text.primary, 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Category Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  📁 CATEGORIES
                </Typography>
                <FormGroup>
                  {categories.map((category) => (
                    <FormControlLabel
                      key={category}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.categories.includes(category)}
                          onChange={() => handleFilterChange('categories', category)}
                          sx={{
                            color: 'primary.main',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            }
                          }}
                        />
                      }
                      label={
                        <Box
                          className="flex items-center justify-between w-full"
                          sx={{ width: "100%" }}
                        >
                          <span
                            className="truncate max-w-[120px]"
                            style={{ color: theme.palette.text.primary }}
                          >
                            {category}
                            {category !== 'Miscellaneous' &&
                              ` (${getItemShortcut(category)})`}
                          </span>

                          <Chip
                            size="small"
                            label={categoryCounts[category] || 0}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme?.palette?.mode === 'dark' ? '#c5dbe0' : '#2558a8',
                              fontWeight: 600,
                              borderRadius: '8px',
                              backdropFilter: 'blur(10px)',
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        marginBottom: "4px",
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "100%",
                        }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Metal Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  💎 METAL TYPE
                </Typography>
                <FormGroup>
                  {metals.map((metal) => (
                    <FormControlLabel
                      key={metal}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.metals.includes(metal)}
                          onChange={() => handleFilterChange('metals', metal)}
                          sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': {
                              color: 'secondary.main',
                            }
                          }}
                        />
                      }
                      label={
                        <Box className="flex justify-between w-full" sx={{ width: "100%" }}>
                          <span style={{ color: theme.palette.text.primary }}>
                            {metal} ({getMetalShortcut(metal)})
                          </span>
                          <Chip
                            size="small"
                            label={metalCounts[metal] || 0}
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: 'secondary.main',
                              fontWeight: 600,
                              borderRadius: '8px',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        marginBottom: "4px",
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "100%",
                        }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Sold Status Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  📊 SOLD STATUS
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('sold')}
                        onChange={() => handleFilterChange('soldStatus', 'sold')}
                        sx={{
                          color: '#10b981',
                          '&.Mui-checked': {
                            color: '#10b981',
                          }
                        }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Sold</span>
                        <Chip
                          size="small"
                          label={soldStatusCounts.sold}
                          sx={{
                            backgroundColor: alpha('#10b981', 0.1),
                            color: '#10b981',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </Box>
                    }
                    sx={{
                      marginBottom: "4px",
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "100%",
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('unsold')}
                        onChange={() => handleFilterChange('soldStatus', 'unsold')}
                        sx={{
                          color: '#f59e0b',
                          '&.Mui-checked': {
                            color: '#f59e0b',
                          }
                        }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Not Sold</span>
                        <Chip
                          size="small"
                          label={soldStatusCounts.unsold}
                          sx={{
                            backgroundColor: alpha('#f59e0b', 0.1),
                            color: '#f59e0b',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </Box>
                    }
                    sx={{
                      marginBottom: "4px",
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "100%",
                      }
                    }}
                  />
                </FormGroup>
              </Box>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item size={{ xs: 12, md: 9 }}>
            <Paper
              sx={{
                ...glassStyles.paper,
                padding: '25px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Header */}
              <Box className="mb-8">
                <Box className="flex items-center justify-between">
                  {/* Left side: Inventory Dashboard with icon */}
                  <Box className="flex items-center space-x-3">
                    <Box
                      sx={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: theme.shadows[3]
                      }}
                    >
                      <InventoryIcon sx={{ color: 'white', fontSize: '28px' }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" className="font-bold" sx={{ color: 'text.primary' }}>
                        Inventory Dashboard
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Manage and monitor your jewelry inventory
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={isCheckingDuplicates ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    disabled={isCheckingDuplicates}
                    onClick={debouncedDuplicateCheck}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                      borderRadius: '10px',
                      // padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      px: 3,
                      py: 1.5,
                      fontSize: '1rem',
                      boxShadow: theme.shadows[3],

                    }}
                  >
                    {isCheckingDuplicates ? 'Checking...' : 'Check Stock'}
                  </Button>

                  {/* Right side: Silver Transaction Button */}
                  {isSecretMode && <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                    onClick={() => {
                      setSilverModalOpen(true);
                    }}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      borderRadius: '10px',
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: theme.shadows[3],
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                        boxShadow: theme.shadows[5],
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Gross Stock Details
                  </Button>}
                </Box>
              </Box>

              {/* Statistics Cards */}
              <Grid container spacing={1.5} className="mb-8">
                {[
                  {
                    title: 'TOTAL ITEMS',
                    value: statistics.totalItems,
                    icon: <InventoryIcon />,
                    gradient: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                  },
                  {
                    title: 'SOLD ITEMS',
                    value: statistics.soldItems,
                    icon: <TrendingUpIcon />,
                    gradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                  },
                  {
                    title: 'UNSOLD ITEMS',
                    value: statistics.unsoldItems,
                    icon: <LocalOfferIcon />,
                    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  },
                  {
                    title: 'TOTAL WEIGHT',
                    value: `${statistics.totalWeight} gm`,
                    icon: <ScaleIcon />,
                    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  },
                  {
                    title: 'TOTAL FINE',
                    value: `${statistics.totalFine} gm`,
                    icon: <DiamondIcon />,
                    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'
                  }
                ].map((stat, index) => (
                  <Grid item xs={12} sm={6} md={2.4} key={index}>
                    <Card
                      sx={{
                        ...glassStyles.card,
                        padding: '7px',
                        height: '100%'
                      }}
                    >
                      <Box className="flex items-center justify-between">
                        <Box
                          sx={{
                            width: '35px',
                            height: '35px',
                            mr: 1,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: stat.gradient,
                            boxShadow: theme.shadows[2]
                          }}
                        >
                          {React.cloneElement(stat.icon, { sx: { color: 'white', fontSize: '20px' } })}
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            className="font-semibold uppercase tracking-wider"
                            sx={{ color: 'text.secondary' }}
                          >
                            {stat.title}
                          </Typography>
                          <Typography
                            variant="h5"
                            className="font-bold mt-2"
                            sx={{ color: 'text.primary' }}
                          >
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Toolbar */}
              <Box className="flex flex-wrap items-center justify-between mb-6 gap-3">
                <Box className="flex items-center space-x-3">
                  <Typography variant="h5" className="font-bold" sx={{ color: 'text.primary' }}>
                    Inventory Items
                  </Typography>
                  <Chip
                    label={`${filteredData.length} items`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                  {selectedItems.length > 0 && (
                    <Chip
                      label={`${selectedItems.length} selected`}
                      size="small"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: 'white',
                        borderRadius: '8px',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                  )}
                  {loading && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
                </Box>

                <Box className="flex flex-wrap gap-2">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={() => navigate('/addItem')}
                  >
                    Add New Item
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.mode === 'dark' ? '#6366f1' : '#0284c7'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA'} 0%, ${theme.palette.mode === 'light' ? '#7C3AED' : '#8B5CF6'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleExport}
                  >
                    Export Data
                  </Button>
                </Box>
              </Box>

              {/* Category Sections */}
              {loading ? (
                <Box className="text-center py-12">
                  <CircularProgress sx={{ color: 'primary.main' }} />
                  <Typography className="mt-4" sx={{ color: 'text.secondary' }}>
                    Loading inventory data...
                  </Typography>
                </Box>
              ) : filteredData.length === 0 ? (
                <Box className="text-center py-12">
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.1)} 0%, ${alpha(theme.palette.text.primary, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                    }}
                  >
                    <SearchIcon sx={{ fontSize: '36px', color: 'text.disabled' }} />
                  </Box>
                  <Typography className="text-lg mb-4" sx={{ color: 'text.primary' }}>
                    No items found
                  </Typography>
                  <Typography className="mb-6" sx={{ color: 'text.secondary' }}>
                    Try adjusting your search or filters
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      color: 'primary.main',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: '12px',
                      padding: '8px 24px',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              ) : (
                <Box className="space-y-4">
                  {Object.keys(filteredGroupedData).sort().map((category) => {
                    const items = filteredGroupedData[category];
                    const isExpanded = expandedCategories[category];

                    // Calculate category stats
                    let categoryWeight = 0;
                    let categoryFine = 0;
                    let categorySold = 0;

                    for (let i = 0; i < items.length; i++) {
                      const item = items[i];
                      const weight = parseFloat(item.weight) || 0;
                      const melting = parseFloat(item.melting) || 0;
                      const wastage = parseFloat(item.wastage) || 0;

                      categoryWeight += weight;
                      categoryFine += weight * ((melting + wastage) / 100);
                      if (item.sold === "Yes") categorySold++;
                    }

                    return (
                      <Paper
                        key={category}
                        sx={{
                          ...glassStyles.card,
                          overflow: 'hidden',
                          borderRadius: '16px'
                        }}
                      >
                        {/* Category Header */}
                        <Box
                          className="flex items-center justify-between p-4 cursor-pointer"
                          onClick={() => handleCategoryToggle(category)}
                          sx={{
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          <Box className="flex items-center space-x-3">
                            <Box
                              sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                boxShadow: theme.shadows[2]
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon sx={{ color: 'white' }} />
                              ) : (
                                <ExpandMoreIcon sx={{ color: 'white' }} />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary' }}>
                                {category}
                                {category !== 'Miscellaneous' &&
                                  ` (${getItemShortcut(category)})`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {categorySold} sold • {items.length - categorySold} available
                              </Typography>
                            </Box>
                          </Box>
                          <Box className="flex items-center space-x-4">
                            <Chip
                              label={`${items.length} items`}
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontWeight: 600,
                                borderRadius: '8px',
                                backdropFilter: 'blur(10px)'
                              }}
                            />
                            <Box className="text-right hidden md:block">
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Weight: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                                  {categoryWeight.toFixed(2)} gm
                                </span>
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Fine: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                                  {categoryFine.toFixed(2)} gm
                                </span>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Category Items Table */}
                        <Collapse in={isExpanded}>
                          <Box className="p-4">
                            <div
                              className={`ag-theme-premium-${theme.palette.mode}`}
                              style={{
                                height: Math.min(400, Math.max(200, items.length * 50 + 50)),
                                width: '100%',
                                borderRadius: '12px',
                                overflow: 'hidden'
                              }}
                            >
                              <AgGridReact
                                rowData={items}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                rowSelection="multiple"
                                suppressRowClickSelection={false}
                                suppressCellFocus={true}
                                onGridReady={(params) => handleGridReady(category, params)}
                                onSelectionChanged={handleSelectionChanged(category, items)}
                                pagination={items.length > 50}
                                paginationPageSize={50}
                                domLayout="normal"
                                headerHeight={50}
                                rowHeight={50}
                                suppressScrollOnNewData={true}
                                animateRows={true}
                                rowMultiSelectWithClick={true}
                                suppressRowDeselection={false}
                              />
                            </div>
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Edit Item Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(148, 163, 184, 0.2)',
            boxShadow: theme.shadows[6],
            borderRadius: '20px'
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          Edit Item: {editingItem?.serialNo || editingItem?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Serial No"
                  value={editFormData.serialNo || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, serialNo: e.target.value }))}
                  size="small"
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gross Weight (gm)"
                  type="text"
                  value={editFormData.grossWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, grossWeight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Less Weight (gm)"
                  type="text"
                  value={editFormData.lessWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, lessWeight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (gm)"
                  type="text"
                  value={editFormData.weight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, weight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Metal"
                  value={editFormData.metal || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, metal: e.target.value }))}
                  size="small"
                  sx={{ mb: 2, width: '210px' }}
                >
                  {metalTypes.map((metal) => (
                    <MenuItem key={metal.id} value={metal.name}>
                      {metal.name} ({metal.shortcut})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HUID"
                  value={editFormData.huid || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, huid: e.target.value }))}
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.warning.main,
                        borderWidth: '2px'
                      }
                    }
                  }}
                  helperText={getBypassFieldsHelpText('huid')}
                  FormHelperTextProps={{
                    sx: {
                      color: theme.palette.warning.main,
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={editFormData.sold || 'No'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, sold: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="Yes">Sold</MenuItem>
                  <MenuItem value="No">Available</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  value={editFormData.supplierName || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Melting %"
                  type="text"
                  value={editFormData.melting || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, melting: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Buying Rate"
                  type="text"
                  value={editFormData.buyingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, buyingRate: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gold Rate"
                  type="text"
                  value={editFormData.goldCuttingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, goldCuttingRate: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Wastage %"
                  type="text"
                  value={editFormData.wastage || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, wastage: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Making Charges %"
                  type="text"
                  value={editFormData.makingCharges || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, makingCharges: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>

              {/* Conditionally render Silver-specific fields */}
              {editFormData.metal === 'Silver' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Silver Rate"
                      type="text"
                      value={editFormData.silverRate || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, silverRate: e.target.value }))}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Labour"
                      type="text"
                      value={editFormData.labour || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, labour: e.target.value }))}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          background: alpha(theme.palette.text.primary, 0.05),
          padding: '16px 24px',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          <Button
            onClick={handleCancelEdit}
            sx={{
              color: 'text.secondary',
              borderColor: alpha(theme.palette.divider, 0.5),
              borderRadius: '12px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSave(editingItem?.id)}
            variant="contained"
            sx={{
              ...(theme.palette.mode === 'light'
                ? glassStyles.button.light.contained
                : glassStyles.button.dark.contained),
              borderRadius: '12px',
              padding: '8px 24px',
              fontWeight: 600,
              '&:hover': {
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.9
            ),
            color: 'white',
            borderRadius: '12px',
            boxShadow: theme.shadows[4],
            border: '1px solid',
            borderColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.5
            )
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isProcessingAction && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
            Processing {pendingAction}...
          </Typography>
        </Box>
      )}
      <OTPModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setPendingAction(null);
          setPendingItemData(null);
        }}
        actionType={pendingAction}
        itemData={pendingItemData}
        onVerifySuccess={handleOTPVerificationSuccess}
      />
      {/* Gross Stock Details Dialog - Updated to use Gross Stock data */}
      <Dialog
        open={silverModalOpen}
        onClose={() => setSilverModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            overflow: 'hidden',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{
          ...(theme.palette.mode === 'light'
            ? glassStyles.button.light.contained
            : glassStyles.button.dark.contained),
          color: 'white',
          padding: '20px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(theme.palette.mode === 'light'
                    ? glassStyles.button.light.contained
                    : glassStyles.button.dark.contained),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <InventoryIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
                    Gross Stock Details
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Track all stock items with piece and weight tracking
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setSilverModalOpen(false)}
                sx={{
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Gross Stock Items Table */}
          <Box sx={{
            p: 3,
            background: alpha(theme.palette.background.paper, 0.8),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Stock Items Overview
              </Typography>
              <Chip
                label={`${grossStock?.length || 0} items`}
                size="small"
                sx={{
                  background: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  fontWeight: 600
                }}
              />
            </Box>

            <Paper sx={{
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '12px',
              background: alpha(theme.palette.background.paper, 0.5)
            }}>
              <TableContainer sx={{ maxHeight: '300px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                    }}>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5 }}>
                        Item Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5 }}>
                        Metal
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5, textAlign: 'center' }}>
                        Current Stock (Pieces)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5, textAlign: 'center' }}>
                        Current Weight (g)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grossStock && grossStock.length > 0 ? (
                      grossStock.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{
                            '&:hover': {
                              background: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                        >
                          <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {item.itemName}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.metal || 'N/A'}
                              size="small"
                              sx={{
                                background: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
                            {item.currentStock || 0}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
                            {parseFloat(item.currentWeight || 0).toFixed(2)} gm
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            No gross stock items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          {/* Split View - Two Tables Side by Side for Transactions */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            minHeight: '400px'
          }}>
            {/* Left Side - Stock Added (Add transactions) */}
            <Box sx={{
              flex: 1,
              borderRight: { lg: `1px solid ${alpha(theme.palette.divider, 0.2)}` },
              borderBottom: { xs: `1px solid ${alpha(theme.palette.divider, 0.2)}`, lg: 'none' }
            }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ color: theme.palette.success.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Stock Added
                    </Typography>
                    <Chip
                      label={`${grossStockTransactions?.filter(t => t.operation === 'add').length || 0} entries`}
                      size="small"
                      sx={{
                        background: alpha(theme.palette.success.main, 0.2),
                        color: theme.palette.success.main,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '300px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Pieces</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Weight</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Party</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grossStockTransactions?.filter(t => t.operation === 'add').map((row) => {
                        const item = grossStock?.find(item => item.id === row.itemId);
                        const itemName = item?.itemName || row.itemName || row.itemId || 'Unknown';
                        return (
                          <TableRow key={row.id} hover sx={{ '&:hover': { background: alpha(theme.palette.success.main, 0.05) } }}>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{itemName}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary' }}>{row.numberOfPieces || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary', fontWeight: 700 }}>{parseFloat(row.weightChanged || 0).toFixed(2)} gm</TableCell>
                            <TableCell sx={{ color: 'text.primary' }}>{row.partyName || 'N/A'}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(!grossStockTransactions || grossStockTransactions.filter(t => t.operation === 'add').length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">No stock added transactions</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            {/* Right Side - Stock Sold (Subtract transactions) */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon sx={{ color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>Stock Sold</Typography>
                    <Chip
                      label={`${grossStockTransactions?.filter(t => t.operation === 'subtract').length || 0} entries`}
                      size="small"
                      sx={{ background: alpha(theme.palette.error.main, 0.2), color: theme.palette.error.main, fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '300px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: alpha(theme.palette.error.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Pieces</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Weight</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Party</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grossStockTransactions?.filter(t => t.operation === 'subtract').map((row) => {
                        const item = grossStock?.find(item => item.id === row.itemId);
                        const itemName = item?.itemName || row.itemName || row.itemId || 'Unknown';
                        return (
                          <TableRow key={row.id} hover sx={{ '&:hover': { background: alpha(theme.palette.error.main, 0.05) } }}>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{itemName}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary' }}>{row.numberOfPieces || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary', fontWeight: 700 }}>{parseFloat(row.weightChanged || 0).toFixed(2)} gm</TableCell>
                            <TableCell sx={{ color: 'text.primary' }}>{row.partyName || 'N/A'}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(!grossStockTransactions || grossStockTransactions.filter(t => t.operation === 'subtract').length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">No stock sold transactions</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          background: alpha(theme.palette.background.paper, 0.5)
        }}>
          <Button
            onClick={() => setSilverModalOpen(false)}
            sx={{
              color: 'text.secondary',
              borderRadius: '10px',
              px: 3,
              '&:hover': {
                background: alpha(theme.palette.text.secondary, 0.1)
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <DuplicateCheckDialog />
    </Box>
  );
};

export default InventoryPage;